/**
 * Abstract service interface for access code management
 * This interface is database-agnostic and can be swapped for different implementations
 * 
 * When migrating to company database, create accessCodeService.company.js
 * implementing this same interface and update the export below.
 */

// Import the Supabase implementation (temporary solution)
import accessCodeServiceSupabase from './accessCodeService.supabase';

// Export the current implementation
// To migrate: replace this import with accessCodeService.company.js
const accessCodeService = accessCodeServiceSupabase;

/**
 * Access code data structure
 * @typedef {Object} AccessCode
 * @property {string} id - Unique identifier
 * @property {string} code - Access code string
 * @property {string|null} expires_at - Expiration date (YYYY-MM-DD) or null
 * @property {number|null} max_valuations - Maximum valuations allowed or null for unlimited
 * @property {boolean} is_active - Whether the code is active
 * @property {string} created_at - Creation timestamp
 * @property {string} updated_at - Last update timestamp
 */

/**
 * Validation result structure
 * @typedef {Object} ValidationResult
 * @property {boolean} valid - Whether the code is valid
 * @property {boolean} expired - Whether the code is expired
 * @property {string} [code] - The validated code
 * @property {number} [maxValuations] - Maximum valuations allowed
 * @property {string} [expirationDate] - Expiration date if expired
 */

/**
 * Usage tracking record
 * @typedef {Object} UsageRecord
 * @property {string} id - Unique identifier
 * @property {string} access_code_id - Reference to access code
 * @property {string} used_at - Timestamp of usage
 * @property {string|null} patent_number - Patent number if applicable
 * @property {string|null} company_name - Company name if applicable
 */

/**
 * Service interface for access code operations
 * All implementations must provide these methods
 */
export const accessCodeServiceInterface = {
  /**
   * Validate an access code
   * @param {string} code - The access code to validate
   * @returns {Promise<ValidationResult>}
   */
  validateAccessCode: async (code) => {
    return accessCodeService.validateAccessCode(code);
  },

  /**
   * Get all access codes
   * @param {Object} options - Query options
   * @param {boolean} [options.includeInactive=false] - Include inactive codes
   * @returns {Promise<AccessCode[]>}
   */
  getAllAccessCodes: async (options = {}) => {
    return accessCodeService.getAllAccessCodes(options);
  },

  /**
   * Get a single access code by ID
   * @param {string} id - Access code ID
   * @returns {Promise<AccessCode|null>}
   */
  getAccessCodeById: async (id) => {
    return accessCodeService.getAccessCodeById(id);
  },

  /**
   * Get a single access code by code string
   * @param {string} code - Access code string
   * @returns {Promise<AccessCode|null>}
   */
  getAccessCodeByCode: async (code) => {
    return accessCodeService.getAccessCodeByCode(code);
  },

  /**
   * Create a new access code
   * @param {Object} data - Access code data
   * @param {string} data.code - Access code string
   * @param {string|null} [data.expires_at] - Expiration date (YYYY-MM-DD)
   * @param {number|null} [data.max_valuations] - Maximum valuations
   * @param {boolean} [data.is_active=true] - Whether code is active
   * @returns {Promise<AccessCode>}
   */
  createAccessCode: async (data) => {
    return accessCodeService.createAccessCode(data);
  },

  /**
   * Update an access code
   * @param {string} id - Access code ID
   * @param {Object} data - Updated data
   * @returns {Promise<AccessCode>}
   */
  updateAccessCode: async (id, data) => {
    return accessCodeService.updateAccessCode(id, data);
  },

  /**
   * Delete an access code
   * @param {string} id - Access code ID
   * @returns {Promise<void>}
   */
  deleteAccessCode: async (id) => {
    return accessCodeService.deleteAccessCode(id);
  },

  /**
   * Get usage count for an access code
   * @param {string} code - Access code string
   * @returns {Promise<number>}
   */
  getValuationUsage: async (code) => {
    return accessCodeService.getValuationUsage(code);
  },

  /**
   * Increment usage count for an access code
   * @param {string} code - Access code string
   * @param {Object} [metadata] - Optional metadata
   * @param {string} [metadata.patent_number] - Patent number
   * @param {string} [metadata.company_name] - Company name
   * @returns {Promise<number>} - New usage count
   */
  incrementValuationUsage: async (code, metadata = {}) => {
    return accessCodeService.incrementValuationUsage(code, metadata);
  },

  /**
   * Get usage records for an access code
   * @param {string} code - Access code string
   * @param {Object} [options] - Query options
   * @param {number} [options.limit] - Limit number of records
   * @returns {Promise<UsageRecord[]>}
   */
  getUsageRecords: async (code, options = {}) => {
    return accessCodeService.getUsageRecords(code, options);
  },
};

// Export the service instance
export default accessCodeServiceInterface;

